/**
 * "Maestro Verified" badge matching by manufacturer + model.
 *
 * Room names ("Living Room") never identify a device, so the badge matches the
 * renderer's reported manufacturer + model first and falls back to the friendly
 * name only when no model match exists. Badge display only — never gates playback.
 */
import {
  extractZoneManufacturerHint,
  isBrandLevelEntry,
  isDegenerateDisplayName,
  matchLiveZoneToVerifiedCatalog,
  matchZoneNameToVerified,
  normalizeRendererNameForMatch,
  type LiveDiscoveredZone,
  type LiveZoneProfile,
  type VerifiedDeviceEntry,
  type ZoneVerifiedMatchResult,
} from './verifiedDevicesUtils';

/** Mirrors mycelium maestro_manufacturer_aliases: UPnP-reported maker → catalog brand. */
const MANUFACTURER_FOLD: Record<string, string> = {
  linkplay: 'wiim',
  'linkplay technology': 'wiim',
  'pixel magic': 'lumin',
  'pixel magic systems': 'lumin',
  lenbrook: 'bluesound',
  'lenbrook industries': 'bluesound',
  'naim audio': 'naim',
  'linn products': 'linn',
  'audio partnership': 'cambridge audio',
  'nad electronics': 'nad',
  'nad electronics international': 'nad',
  'hegel music systems': 'hegel',
  'lyngdorf audio': 'lyngdorf',
  'melco syncrets': 'melco',
  buffalo: 'melco',
  tvlogic: 'aurender',
  conversdigital: 'ps audio',
  psaudio: 'ps audio',
  'jf light industries': 'ps audio',
  'denon/marantz': 'denon',
};

const CORPORATE_SUFFIX = /[,.]?\s+(?:inc|ltd|limited|gmbh|co|corp|corporation|llc|ab|sa|plc|s\.a)\.?$/;

export function foldVerifiedManufacturer(raw: string | null | undefined): string {
  let m = normalizeRendererNameForMatch(raw);
  for (let i = 0; i < 2 && CORPORATE_SUFFIX.test(m); i++) m = m.replace(CORPORATE_SUFFIX, '').trim();
  return MANUFACTURER_FOLD[m] || m;
}

function withoutBrand(name: string, brand: string): string {
  return brand && name.startsWith(`${brand} `) ? name.slice(brand.length + 1) : name;
}

/** Catalog entry for a renderer's reported manufacturer + model, or null. */
export function matchVerifiedByModel(
  manufacturer: string | null | undefined,
  model: string | null | undefined,
  devices: VerifiedDeviceEntry[],
): VerifiedDeviceEntry | null {
  const m = normalizeRendererNameForMatch(model);
  if (!m || isDegenerateDisplayName(model)) return null;
  const zm = foldVerifiedManufacturer(manufacturer);
  const zoneModel = withoutBrand(m, zm);

  for (const device of devices) {
    if (isBrandLevelEntry(device)) continue;
    const dm = foldVerifiedManufacturer(device.manufacturer);
    const name = normalizeRendererNameForMatch(device.device_name);
    if (!name) continue;
    if (zm) {
      if (dm !== zm) continue;
      if (withoutBrand(name, dm) === zoneModel) return device;
    } else if (m.includes(' ') && (name === m || `${dm} ${withoutBrand(name, dm)}` === m)) {
      // No reported maker: only a full brand+model string may match.
      return device;
    }
  }
  return null;
}

/** Manufacturer + model first; the friendly names only as a fallback. */
export function matchZoneVerifiedBadge(
  zone: { name?: string | null; metadata?: Record<string, unknown> | null },
  devices: VerifiedDeviceEntry[],
  index: Map<string, VerifiedDeviceEntry>,
  friendlyNames: Array<string | null | undefined> = [zone.name],
): VerifiedDeviceEntry | null {
  const meta = zone.metadata || {};
  const byModel = matchVerifiedByModel(meta.manufacturer as string, meta.model as string, devices);
  if (byModel) return byModel;
  for (const name of friendlyNames) {
    const hit = matchZoneNameToVerified(name, index);
    if (hit) return hit;
  }
  return null;
}

/** Devices page: live discovery manufacturer + model first, then the name-based catalog match. */
export function matchLiveZoneVerifiedBadge(
  profile: LiveZoneProfile,
  discovered: LiveDiscoveredZone | null | undefined,
  devices: VerifiedDeviceEntry[],
): ZoneVerifiedMatchResult {
  const byModel = matchVerifiedByModel(extractZoneManufacturerHint(discovered), discovered?.metadata?.model, devices);
  const byName = matchLiveZoneToVerifiedCatalog(profile, discovered, byModel ? [] : devices);
  return byModel ? { ...byName, match: byModel } : byName;
}
